import type { z } from 'zod';
import { problemSchema, SNAPSHOT_DIR, snapshotFile, type ProblemDto } from '@crm/shared/contracts';
import { API_BASE, STATIC_DEMO } from './config.ts';

/** A failed API call: an RFC 9457 problem, or a network failure (`status` 0). */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    message: string,
    readonly problem: ProblemDto | null = null,
    readonly requestId: string | null = null,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export type QueryParams = Record<string, string | readonly string[] | undefined>;

export interface RequestOptions<T> {
  method?: 'GET' | 'POST';
  params?: QueryParams;
  body?: unknown;
  /** Contract of the response. It is enforced in development and tests (docs/DASHBOARD_SPECIFICATION.md §9). */
  schema?: z.ZodType<T>;
  signal?: AbortSignal;
}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | undefined;

/** Called once when any request answers 401, so the app can send the user to the login page. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | undefined): void {
  onUnauthorized = handler;
}

const shouldValidate = (): boolean => import.meta.env.DEV || import.meta.env.MODE === 'test';

export function buildQuery(params: QueryParams | undefined): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(params ?? {})) {
    if (value === undefined) continue;
    if (typeof value === 'string') search.append(name, value);
    else for (const item of value) search.append(name, item);
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

async function readProblem(response: Response): Promise<ProblemDto | null> {
  try {
    const parsed = problemSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Problem type of a view the static demo has no exported data for (a custom period, a filter). */
export const NOT_IN_SNAPSHOT = 'static-demo/not-in-snapshot';

/** Static demo (ADR-027): the exported response for this request, read from a file. */
async function snapshotRequest<T>(path: string, options: RequestOptions<T>): Promise<T> {
  if ((options.method ?? 'GET') !== 'GET') throw new ApiError(405, 'Read-only demo');
  const file = snapshotFile(path, buildQuery(options.params));
  let response: Response;
  try {
    response = await fetch(`${import.meta.env.BASE_URL}${SNAPSHOT_DIR}/${file}`, {
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Network error');
  }
  if (!response.ok || !response.headers.get('content-type')?.includes('json')) {
    const problem = { type: NOT_IN_SNAPSHOT, title: 'Not in the demo snapshot', status: 404 };
    throw new ApiError(404, problem.title, problem);
  }
  return (await response.json()) as T;
}

export async function apiRequest<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  if (STATIC_DEMO) return snapshotRequest(path, options);
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') {
    // The CSRF header the API requires on every state-changing request (docs/SECURITY.md §2).
    headers['x-requested-with'] = 'crm-dashboard';
    headers['content-type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}${buildQuery(options.params)}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Network error');
  }

  if (!response.ok) {
    const problem = await readProblem(response);
    const retryAfter = Number(response.headers.get('retry-after'));
    const error = new ApiError(
      response.status,
      problem?.title ?? `HTTP ${response.status}`,
      problem,
      problem?.requestId ?? response.headers.get('x-request-id'),
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
    );
    if (response.status === 401) onUnauthorized?.();
    throw error;
  }

  const json: unknown = await response.json();
  return options.schema && shouldValidate() ? options.schema.parse(json) : (json as T);
}
